import Papa from 'papaparse';
import {
  getDefaultTeam,
  getGender,
  formatLastName,
  formatBirthDate,
  formatMeetEntryMark,
} from '../../../utils/meetUtils';
import { computeAge } from '../../../utils/dateUtils';

/**
 * buildRelayKey:
 * Crea una "clave" para agrupar a varios inscritos que forman parte del
 * mismo relevo (mismo teamCode, eventCode, asignacion/relayLetter, etc.).
 */
function buildRelayKey({ teamCode, eventCode, relayLetter, relayGender, catNumber }) {
  // Aseguramos que catNumber sea parte de la clave
  return `${teamCode}|${relayLetter}|${relayGender}${eventCode}|${catNumber || ''}`;
}

/**
 * Crea la estructura inicial para un relevo agrupado.
 * rowPart1 => las columnas fijas (1..15)
 * runners => array de 8 espacios (uno por cada posible corredor)
 */
function createEmptyRelayGroup(rowPart1) {
  return {
    rowPart1,
    // array de 8 (cada uno en principio null), se llenará con la info de cada corredor
    runners: Array(8).fill(null),
  };
}

/**
 * parsePosition:
 * Convierte un string como "Integrante 1", "Integrante 2", "Suplente 1", etc.
 * en un índice de 0 a 7 (máximo 8 corredores).
 *
 * Ajusta la lógica si tu naming difiere.
 */
function parsePosition(posString = '', maxMiembros = 4, numeroSuplentes = 2) {
  const lower = posString.toLowerCase().trim();
  
  // 1) Detectamos si es "Integrante X"
  let match = lower.match(/^integrante\s+(\d+)$/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (!isNaN(num) && num >= 1 && num <= maxMiembros) {
      return num - 1;
    }
  }

  // 2) Detectamos si es "Suplente X"
  match = lower.match(/^suplente\s+(\d+)$/);
  if (match) {
    const num = parseInt(match[1], 10);
    if (!isNaN(num) && num >= 1 && num <= numeroSuplentes) {
      return maxMiembros + (num - 1);
    }
  }

  return -1;
}

// Add new function to check relay capacity
function getRelayCapacity(relayKey, inscripciones, maxMiembros, numeroSuplentes) {
  let count = 0;
  inscripciones.forEach(insc => {
    if (!Array.isArray(insc.relevosData)) return;
    
    insc.relevosData.forEach(relevo => {
      const currentKey = buildRelayKey({
        teamCode: getDefaultTeam(insc.equipo).teamCode,
        eventCode: relevo.eventCode || '',
        relayLetter: relevo.asignacion || 'A',
        relayGender: relevo.equipoTipo?.toLowerCase().includes('varonil') ? 'M' : 
                     relevo.equipoTipo?.toLowerCase().includes('femenil') ? 'F' : 'X',
        catNumber: insc.catNumber
      });

      if (currentKey === relayKey && relevo.posicion) {
        count++;
      }
    });
  });
  
  return {
    current: count,
    maxTotal: maxMiembros + numeroSuplentes,
    isFull: count >= (maxMiembros + numeroSuplentes)
  };
}

// Then in your component, you can use it like this:
export function exportMeetQ(inscripciones, competitionName, opcionesRelevos = {}) {
  const { maxMiembros = 4, numeroSuplentes = 2 } = opcionesRelevos;
  const maxTotalMiembros = maxMiembros + numeroSuplentes;
  
  const relayMap = {};
  
  inscripciones.forEach((insc) => {
    if (!Array.isArray(insc.relevosData)) return;
  
    insc.relevosData.forEach((relevo) => {
      const { teamCode, teamName } = getDefaultTeam(insc.equipo);
      const relayLetter = relevo.asignacion || 'A';
  
      let relayGender = '';
      if (relevo.equipoTipo) {
        const lt = relevo.equipoTipo.toLowerCase();
        if (lt.includes('varonil'))  relayGender = 'M';
        else if (lt.includes('femenil')) relayGender = 'F';
        else if (lt.includes('mixto'))    relayGender = 'X';
      }
      if (!relayGender) {
        relayGender = getGender(relevo.rama || insc.rama) || 'X';
      }
  
      const key = buildRelayKey({
        teamCode,
        eventCode: relevo.eventCode || '',
        relayLetter,
        relayGender,
        catNumber: insc.catNumber
      });
  
      // Check relay capacity before proceeding
      const capacity = getRelayCapacity(key, inscripciones, maxMiembros, numeroSuplentes);
      if (capacity.isFull) {
        return; // Skip if relay is full
      }
  
      if (!relayMap[key]) {
        const rowPart1 = [];
        rowPart1.push('Q');
        // rowPart1 (cols 1..15)
        rowPart1.push(teamCode);                    // col2
        rowPart1.push(teamName);                    // col3
        rowPart1.push(relayLetter);                 // col4
        rowPart1.push(relayGender);                 // col5
        rowPart1.push('');                          // col6 -> Relay Age (vacio)
        rowPart1.push(relevo.eventCode || '');      // col7
        rowPart1.push(formatMeetEntryMark(relevo)); // col8 -> marca de siembra
        rowPart1.push('M');                         // col9 -> métrico
        // 10) division => catNumber (en relevo o insc)
        const division = relevo.catNumber
          ? String(relevo.catNumber)
          : (insc.catNumber ? String(insc.catNumber) : '');
        rowPart1.push(division);                    // col10
        rowPart1.push('');                          // col11 finishPlace
        rowPart1.push(relevo.status || '');         // col12 declaration
        rowPart1.push('');                          // col13 entryNote
        rowPart1.push('');                          // col14 spare
        rowPart1.push('');                          // col15 spare
  
        relayMap[key] = createEmptyRelayGroup(rowPart1);
      }
  
      const idx = parsePosition(relevo.posicion, maxMiembros, numeroSuplentes);
      if (idx >= 0 && idx < maxTotalMiembros) {
        const currentRunners = relayMap[key]?.runners.filter(r => r !== null).length || 0;
        if (currentRunners < capacity.maxTotal) {
          relayMap[key].runners[idx] = {
            inscripcion: insc,
          };
        }
      }
    });
  });
  // 7) Convertimos cada relevo en UNA fila CSV
  const csvRows = [];
  Object.values(relayMap).forEach((relayGroup) => {
    const { rowPart1, runners } = relayGroup;
  // runners es array de 8 (null si vacío)
    const rowCorredores = [];
  for (let i = 0; i < 8; i++) {
    const data = runners[i];
    if (!data) {
      // 8 columnas vacías
      rowCorredores.push('', '', '', '', '', '', '', '');
      continue;
    }
  // Extraemos la info de "data.inscripcion"
    const { inscripcion } = data;
  // Nombre y apellido
    const lastN = formatLastName(inscripcion.apellido || '');
    const firstN = inscripcion.nombre || '';
    const middleI = '';
    // Determinamos género (M/F)
    const cGender = getGender(inscripcion.rama) || 'M';
    // Birthdate
    const birth = formatBirthDate(inscripcion);
  let ageVal = '';
    if (!birth) {
      const aVal = computeAge(
        inscripcion.anioNacimiento,
        inscripcion.mesNacimiento,
        inscripcion.diaNacimiento
      );
      ageVal = aVal ? String(aVal) : '';
    }
  // CompetitorNum si lo manejas
    const competitorNum = inscripcion.competitorNum || '';
  rowCorredores.push(
    lastN,         // col 16,24,32...
    firstN,        // col 17,25,33...
    middleI,       // col 18,26,34...
    cGender,       // col 19,27,35...
    birth,         // col 20,28,36...
    ageVal,        // col 21,29,37...
    '',            // col 22 schoolYear
    competitorNum  // col 23
  );
  }
  const fullRow = [...rowPart1, ...rowCorredores];
  csvRows.push(fullRow);
  });
  // 8) Convertimos a CSV con delimitador ";"
  let csv = Papa.unparse(csvRows, {
    quotes: false,
    header: false,
    delimiter: ';',
  });
  csv = csv.replace(/"/g, '');
  // 9) Descargar
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${competitionName}_inscritos_MeetManagerQ.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
